import { map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { CookieConsentService } from '@core/services/cookie-consent/cookie-consent.service';
import { PlatformService } from '@core/services/platform.service';
import { TrackBasketRequestBody } from '@core/services/relewise/relewise-tracking.model';
import { StorageService } from '@core/services/storage.service';
import { UrlHelperService } from '@core/services/url-helper.service';
import { TrackingEntityType, TrackingResponse } from '@models/api-search.models';
import { SiteContextService } from '@core/services/site-context.service';
import { removeEmpty } from 'src/app/@utils/remove-empty.helper';

@Injectable({
    providedIn: 'root'
})
export class RelewiseTrackingService {
    private readonly storageKey = 'relewise-storage-key';
    private readonly agillicIdSearchParameter = 'ag_id';
    private userTempId = '';
    private consent: boolean;
    private pageType: string;
    public agillicId: string | null = null;

    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private cookieConsentService: CookieConsentService,
        private urlHelperService: UrlHelperService,
        private platformService: PlatformService,
        private siteContextService: SiteContextService
    ) {
        const { searchParams } = this.urlHelperService.getCurrentUrl();
        this.agillicId = searchParams.get(this.agillicIdSearchParameter) || null;

        this.cookieConsentService.consentStatus$
            .pipe(map(status => Boolean(status.cookie_cat_marketing && status.cookie_cat_statistic)))
            .subscribe(consent => {
                this.consent = consent;
            });

        this.siteContextService.context$.pipe(map(({ type }) => type)).subscribe(type => (this.pageType = type));
    }

    /**
     * Generic push wrapper for relewise tracking requests
     * Only track browser requests, add relevant params and stores
     * temporary user ID in storage
     *
     * if consent is true, this is then passed with future request
     * @param endpoint
     * @param body
     * @param params
     */
    public push(endpoint: string, body?: { [key: string]: any } | null, params?: Params): void {
        if (this.platformService.isServer) {
            return;
        }

        this.http
            .post<TrackingResponse>(endpoint, body, { params: this.getRelewiseTrackingParams(params) })
            .pipe(tap(({ temporaryId }) => this.updateTempId(temporaryId)))
            .subscribe({ error: e => console.error(e) });
    }

    /**
     * Track brand pages by supplying a brand id
     * @param brandId
     */
    public trackBrand(brandId: string): void {
        this.push('search-ms/tracking/brand', { brandId });
    }

    /**
     * Track category pagesby supplying the Umbraco pagekey
     * The mapping to a category will be handled by BE
     * @param pageKey
     */
    public trackCategory(pageKey: string): void {
        this.push('search-ms/tracking/category', null, { pageKey });
    }

    /**
     * Track product views by supplying a product id
     * @param productNo
     */
    public trackProduct(productNo: string): void {
        this.push(`search-ms/tracking/product/${productNo}`, null);
    }

    /**
     * Track user email. Email is optional since agilic id will be supplied if available
     * @param email
     */
    public trackUser(email?: string): void {
        const params = email ? { email } : {};

        this.push('search-ms/tracking/user', null, params);
    }

    /**
     * Track agilic user. will only fire if agilic id is present
     *
     */
    public trackAgilicUser(): void {
        if (!this.agillicId) {
            return;
        }
        this.push('search-ms/tracking/user', null);
    }

    /**
     * Track checkout or reservation
     * @param id
     * @param requestBody
     * @param entityType
     * @param basketId
     */
    public trackBasket(entityType: TrackingEntityType, id: string, requestBody: TrackBasketRequestBody): void {
        const lineItems = (requestBody.lineItems || [])
            .filter(line => line.lineType === 'Normal')
            ?.map(({ variantNo, quantity, priceAmountRaw }) => ({
                productNo: variantNo,
                quantity,
                lineTotal: priceAmountRaw
            }));

        this.push(`search-ms/tracking/${entityType}/${id}`, { ...requestBody, lineItems });
    }

    /**
     * Get relevant relewise params, when requesting products
     * We differentiate on two types of searches "instantSearch"
     * on all pages other than searchresult and "searchPage"
     * on search result page
     */
    public getRelewiseSearchParams(): Params {
        const { temporaryId, consent } = this.getRelewiseTrackingParams();

        const location = this.pageType === 'searchPage' ? this.pageType : 'instantSearch';

        return removeEmpty({ temporaryId, location, consent });
    }

    /**
     * We figure out whether we should add the temp id or not,
     * the temporary id is returned by each tracking request
     * so if it's available and consent is true, we add it to every request
     * to track the user
     * @param additional
     */
    public getRelewiseTrackingParams(additional?: Params): Params {
        return {
            consent: this.consent,
            ...(this.getTempId() && this.consent ? { temporaryId: this.getTempId() } : {}),
            ...(this.agillicId ? { agillicId: this.agillicId } : {}),
            ...(this.pageType ? { location: this.pageType } : {}),
            ...additional
        };
    }

    public updateTempId(id?: string): void {
        if (id && id !== this.userTempId) {
            this.userTempId = id;
            this.storageService.set(this.storageKey, id, { cookieFallback: true });
        }
    }

    private getTempId(): string {
        if (this.userTempId) {
            return this.userTempId;
        }

        const storageId = this.storageService.get(this.storageKey);

        if (storageId) {
            this.userTempId = storageId;
        }

        return this.userTempId;
    }
}
